/**
 * Fajne3D: skrypt motywu (moduł ES, vanilla JS, custom elements).
 *
 * Koszyk: Cart API (/cart/add.js, /cart/update.js, /cart.js) + standardowe zdarzenia Shopify
 * (`CartLinesUpdateEvent` z `@shopify/events`), tak samo jak w Horizon. Dzięki temu licznik
 * w nagłówku, szuflada koszyka i strona koszyka odświeżają się same (Section Rendering API).
 *
 * Każdy komponent sprawdza, czy jego elementy istnieją, i nie rzuca błędów, gdy ich brakuje.
 */
import { StandardEvents, CartLinesUpdateEvent, CartDiscountUpdateEvent } from '@shopify/events';

/* ==========================================================================
   Konfiguracja i narzędzia
   ========================================================================== */

/** @type {Record<string, any>} */
const CFG = (() => {
  try {
    const el = document.getElementById('f3d-config');
    return el ? JSON.parse(el.textContent || '{}') : {};
  } catch (error) {
    console.warn('[f3d] Nie udało się odczytać konfiguracji', error);
    return {};
  }
})();
const I18N = CFG.i18n || {};
const ROUTES = CFG.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartUpdate: '/cart/update' };

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const debounce = (fn, ms) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};
/** Teksty z tłumaczeń mają miejsca na wartości w postaci __klucz__ (bez klamer, bo Liquid ich nie lubi w {{ }}). */
const tpl = (str, map = {}) =>
  String(str || '').replace(/__(\w+)__/g, (m, key) => (key in map ? String(map[key]) : m));
const escapeHTML = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const LOCALE = (CFG.locale || document.documentElement.lang || 'pl').toLowerCase().startsWith('pl') ? 'pl-PL' : 'en-US';
const moneyFmt = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });
const moneyFmt0 = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', minimumFractionDigits: 0, maximumFractionDigits: 0 });
/** @param {number} cents */
const fmt = (cents) => moneyFmt.format((Number(cents) || 0) / 100);
/** @param {number} cents */
const fmtShort = (cents) => ((Number(cents) || 0) % 100 === 0 ? moneyFmt0 : moneyFmt).format((Number(cents) || 0) / 100);

const store = {
  get(key, fallback) {
    try {
      const v = window.sessionStorage.getItem(key);
      return v == null ? fallback : JSON.parse(v);
    } catch (e) {
      return fallback;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* prywatne okno albo brak miejsca: pomijamy */
    }
  }
};

/* ==========================================================================
   Toasty (region aria-live; przy otwartym oknie modalnym toast trafia do niego)
   ========================================================================== */
const ICON_CHECK = '<svg class="f3d-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
const ICON_ALERT = '<svg class="f3d-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 4 2.5 20h19L12 4Z"/><path d="M12 10v4.5M12 17.5h.01"/></svg>';

/**
 * @param {string} message
 * @param {{ type?: 'error' | 'success', emoji?: string, action?: { label: string, href?: string, onClick?: () => void } }} [opts]
 */
function toast(message, opts = {}) {
  if (!message) return;
  let host = $('[data-f3d-toasts]');
  if (!host) {
    host = document.createElement('div');
    host.className = 'f3d-toasts';
    host.setAttribute('data-f3d-toasts', '');
    host.setAttribute('role', 'status');
    host.setAttribute('aria-live', 'polite');
  }
  let modal = null;
  try {
    modal = document.querySelector('dialog:modal');
  } catch (e) {
    modal = null;
  }
  const target = modal || document.body;
  if (host.parentNode !== target) target.appendChild(host);

  const isError = opts.type === 'error';
  const el = document.createElement('div');
  el.className = 'f3d-toast' + (isError ? ' f3d-toast--error' : '');
  if (isError) el.setAttribute('role', 'alert');
  const icon = opts.emoji ? `<span aria-hidden="true">${escapeHTML(opts.emoji)}</span>` : isError ? ICON_ALERT : ICON_CHECK;
  el.innerHTML = `<span class="f3d-toast__icon">${icon}</span><span class="f3d-toast__msg"></span>`;
  $('.f3d-toast__msg', el).textContent = message;
  if (opts.action) {
    const a = document.createElement(opts.action.href ? 'a' : 'button');
    a.className = 'f3d-toast__action';
    a.textContent = opts.action.label;
    if (opts.action.href) a.setAttribute('href', opts.action.href);
    else {
      a.setAttribute('type', 'button');
      a.addEventListener('click', () => {
        opts.action?.onClick?.();
        remove();
      });
    }
    el.appendChild(a);
  }
  host.appendChild(el);
  while (host.children.length > 3) host.firstElementChild?.remove();
  let timer = setTimeout(remove, isError ? 6500 : 3600);
  el.addEventListener('mouseenter', () => clearTimeout(timer));
  el.addEventListener('mouseleave', () => {
    clearTimeout(timer);
    timer = setTimeout(remove, 1800);
  });
  function remove() {
    el.classList.add('is-leaving');
    setTimeout(() => el.remove(), 220);
  }
}

/* ==========================================================================
   Konfetti (canvas; wyłączone przy "Ogranicz ruch")
   ========================================================================== */
/** @param {Element | null} [origin] */
function confetti(origin) {
  if (reduceMotion() || CFG.confetti === false) return;
  let host = document.body;
  try {
    host = document.querySelector('dialog:modal') || document.body;
  } catch (e) {
    host = document.body;
  }
  const c = document.createElement('canvas');
  c.className = 'f3d-confetti';
  c.setAttribute('aria-hidden', 'true');
  host.appendChild(c);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const W = window.innerWidth;
  const H = window.innerHeight;
  c.width = W * dpr;
  c.height = H * dpr;
  const ctx = c.getContext('2d');
  if (!ctx) {
    c.remove();
    return;
  }
  ctx.scale(dpr, dpr);
  const r = origin?.getBoundingClientRect?.() || { left: W / 2, top: H / 3, width: 0, height: 0 };
  const ox = r.left + r.width / 2;
  const oy = r.top + r.height / 2;
  const colors = ['#FFD801', '#111111', '#FF7AB6', '#5BC0FF', '#7BE07B', '#FF9F43', '#FFFFFF'];
  const parts = Array.from({ length: 130 }, () => ({
    x: ox + (Math.random() - 0.5) * Math.max(40, r.width * 0.6),
    y: oy,
    vx: (Math.random() - 0.5) * 13,
    vy: -Math.random() * 13 - 5,
    w: 5 + Math.random() * 6,
    h: 8 + Math.random() * 9,
    rot: Math.random() * 6.28,
    vr: (Math.random() - 0.5) * 0.35,
    c: colors[(Math.random() * colors.length) | 0],
    circle: Math.random() < 0.25
  }));
  const t0 = performance.now();
  let last = t0;
  const frame = (now) => {
    const dt = Math.min(34, now - last) / 16.7;
    last = now;
    const life = now - t0;
    ctx.clearRect(0, 0, W, H);
    ctx.globalAlpha = life > 1700 ? Math.max(0, 1 - (life - 1700) / 600) : 1;
    for (const p of parts) {
      p.vy += 0.38 * dt;
      p.vx *= 0.985;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.c;
      if (p.circle) {
        ctx.beginPath();
        ctx.arc(0, 0, p.w / 2, 0, 6.28);
        ctx.fill();
      } else {
        const k = Math.abs(Math.cos(p.rot * 1.5));
        ctx.fillRect(-p.w / 2, (-p.h / 2) * k, p.w, p.h * k + 1);
      }
      ctx.restore();
    }
    if (life < 2300) requestAnimationFrame(frame);
    else c.remove();
  };
  requestAnimationFrame(frame);
}

/* ==========================================================================
   Koszyk: pobieranie, dodawanie, gratis, progi
   ========================================================================== */
const GIFT_PROP = '_f3d_gift';

const Cart = {
  /** @type {any} */
  last: null,
  /** @type {number | null} */
  lastMerch: null,
  /** @type {Promise<any> | null} */
  pending: null,

  async fetch() {
    if (this.pending) return this.pending;
    this.pending = fetch(`${ROUTES.cart}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error(`Cart ${r.status}`);
        return r.json();
      })
      .then((cart) => {
        this.last = cart;
        return cart;
      })
      .finally(() => {
        this.pending = null;
      });
    return this.pending;
  },

  /** @param {any} item */
  isGift(item) {
    if (!item) return false;
    if (item.properties && item.properties[GIFT_PROP]) return true;
    return Boolean(CFG.giftVariantId) && String(item.variant_id) === String(CFG.giftVariantId);
  },

  /** Wartość koszyka po rabatach, bez linii z gratisem (w groszach). */
  merch(cart) {
    if (!cart) return 0;
    const giftTotal = (cart.items || []).filter((i) => this.isGift(i)).reduce((s, i) => s + (i.final_line_price || 0), 0);
    return Math.max(0, (cart.total_price || 0) - giftTotal);
  },

  /**
   * Wysyła standardowe zdarzenie koszyka, żeby Horizon odświeżył licznik i sekcje koszyka.
   * @param {EventTarget} target
   * @param {'add' | 'update' | 'remove'} action
   * @param {any[]} lines
   * @param {'product' | 'cart'} [context]
   */
  announce(target, action, lines, context = 'product') {
    const deferred = CartLinesUpdateEvent.createPromise();
    try {
      target.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));
    } catch (error) {
      console.warn('[f3d] Nie udało się wysłać zdarzenia koszyka', error);
    }
    return deferred;
  },

  /**
   * Dodaje wariant do koszyka (z obsługą błędów Cart API po polsku).
   * @param {number | string} variantId
   * @param {{ quantity?: number, properties?: Record<string, string>, source?: Element | null }} [opts]
   * @returns {Promise<boolean>}
   */
  async add(variantId, opts = {}) {
    const quantity = Math.max(1, Number(opts.quantity) || 1);
    const source = opts.source || document;
    const deferred = this.announce(source, 'add', [{ merchandiseId: String(variantId), quantity }], 'product');
    try {
      const res = await fetch(`${ROUTES.cartAdd}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity, properties: opts.properties || {} }] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        const cart = await this.fetch().catch(() => null);
        if (cart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, itemCount: cart.item_count, source: 'f3d' }
          });
        } else deferred.reject(new Error('cart'));
        toast(data.description || data.message || I18N.addError, { type: 'error' });
        return false;
      }
      const cart = await this.fetch();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, itemCount: cart.item_count, source: 'f3d', productId: data.items?.[0]?.product_id }
      });
      return true;
    } catch (error) {
      deferred.reject(error);
      toast(I18N.networkError, { type: 'error' });
      return false;
    }
  },

  /**
   * Dodanie z formularza produktu (z właściwościami, np. personalizacją).
   * @param {FormData} fd
   * @param {Element} [source]
   * @returns {Promise<{ ok: boolean, message?: string }>}
   */
  async addForm(fd, source) {
    const quantity = Math.max(1, Number(fd.get('quantity')) || 1);
    const deferred = this.announce(source || document, 'add', [{ merchandiseId: String(fd.get('id')), quantity }], 'product');
    try {
      const res = await fetch(`${ROUTES.cartAdd}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      const cart = await this.fetch().catch(() => null);
      if (!res.ok || data.status) {
        if (cart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, itemCount: cart.item_count, source: 'f3d' }
          });
        } else deferred.reject(new Error('cart'));
        const message = data.description || data.message || I18N.addError;
        toast(message, { type: 'error' });
        return { ok: false, message };
      }
      if (!cart) throw new Error('cart');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, itemCount: cart.item_count, source: 'f3d', productId: data.product_id }
      });
      return { ok: true };
    } catch (error) {
      deferred.reject(error);
      toast(I18N.networkError, { type: 'error' });
      return { ok: false, message: I18N.networkError };
    }
  },

  /**
   * Zmiana ilości kilku linii naraz (np. gratis) przez /cart/update.js.
   * @param {Record<string, number>} updates - klucz linii → ilość
   */
  async update(updates) {
    const res = await fetch(`${ROUTES.cartUpdate}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!res.ok) throw new Error(`update ${res.status}`);
    return res.json();
  }
};

/* ---------- Gratis: dodaj od progu, usuń poniżej. Blokada pętli + limit prób. ---------- */
const Gift = {
  syncing: false,
  attempts: [],
  unavailable: store.get('f3d-gift-unavailable', false),

  enabled() {
    return Boolean(CFG.giftVariantId) && Number(CFG.giftThreshold) > 0;
  },

  /** Nie więcej niż 4 zmiany gratisu na 10 s (ochrona przed pętlą). */
  allowed() {
    const now = Date.now();
    this.attempts = this.attempts.filter((t) => now - t < 10000);
    if (this.attempts.length >= 4) return false;
    this.attempts.push(now);
    return true;
  },

  /** @param {any} cart */
  async sync(cart) {
    if (!this.enabled() || this.syncing || !cart) return;
    const items = cart.items || [];
    const giftLines = items.filter((i) => Cart.isGift(i));
    const others = items.filter((i) => !Cart.isGift(i));
    const merch = Cart.merch(cart);
    const want = others.length > 0 && merch >= Number(CFG.giftThreshold);

    /** @type {Record<string, number> | null} */
    let updates = null;
    let add = false;
    if (want && giftLines.length === 0 && !this.unavailable) add = true;
    else if (want && (giftLines.length > 1 || giftLines[0]?.quantity !== 1)) {
      updates = {};
      giftLines.forEach((l, i) => {
        updates[l.key] = i === 0 ? 1 : 0;
      });
    } else if (!want && giftLines.length > 0) {
      updates = {};
      giftLines.forEach((l) => {
        updates[l.key] = 0;
      });
    }
    if (!add && !updates) return;
    if (!this.allowed()) return;

    this.syncing = true;
    const deferred = Cart.announce(document, add ? 'update' : 'remove', [{ id: 'f3d-gift', quantity: add ? 1 : 0 }], 'cart');
    try {
      if (add) {
        const res = await fetch(`${ROUTES.cartAdd}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(CFG.giftVariantId), quantity: 1, properties: { [GIFT_PROP]: '1' } }] })
        });
        if (!res.ok) {
          // Gratis wyprzedany albo niedostępny: nie próbujemy w kółko w tej sesji.
          this.unavailable = true;
          store.set('f3d-gift-unavailable', true);
        }
      } else if (updates) {
        await Cart.update(updates);
      }
      const fresh = await Cart.fetch();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
        detail: { didError: false, items: fresh.items, itemCount: fresh.item_count, source: 'f3d-gift' }
      });
      const hasGift = (fresh.items || []).some((i) => Cart.isGift(i));
      if (add && hasGift) toast(I18N.addedGift, { emoji: '🎁' });
      if (!add && !want && !hasGift && giftLines.length) toast(I18N.removedGift, { emoji: '🎁' });
      Goals.update(fresh);
    } catch (error) {
      deferred.reject(error);
      console.warn('[f3d] Synchronizacja gratisu nie powiodła się', error);
    } finally {
      this.syncing = false;
    }
  }
};

/* ---------- Paski progów: stan liczony w JS (strona produktu) i animacja zmian ---------- */
const Goals = {
  /** @param {number} merch */
  state(merch) {
    const free = Number(CFG.freeShipping) || 0;
    const gift = CFG.giftVariantId ? Number(CFG.giftThreshold) || 0 : 0;
    const max = Math.max(free, gift);
    return { free, gift, max, merch, p: max ? Math.min(1, merch / max) : 0 };
  },

  /** @param {ReturnType<typeof Goals.state>} s */
  text(s) {
    const G = I18N.goal || {};
    const amt = (th) => `<strong>${escapeHTML(fmt(Math.max(0, th - s.merch)))}</strong>`;
    const giftOk = s.gift > 0 && s.merch >= s.gift;
    const shipOk = s.free > 0 && s.merch >= s.free;
    if (s.merch >= s.max) {
      if (s.gift > 0 && s.free > 0) return G.done;
      return s.free > 0 ? G.doneShip : G.doneGift;
    }
    if (s.gift > 0 && s.free > 0) {
      if (s.gift <= s.free) return giftOk ? tpl(G.giftThenShip, { amount: amt(s.free) }) : tpl(G.needGift, { amount: amt(s.gift) });
      return shipOk ? tpl(G.shipThenGift, { amount: amt(s.gift) }) : tpl(G.needShip, { amount: amt(s.free) });
    }
    if (s.free > 0) return tpl(G.needShip, { amount: amt(s.free) });
    return tpl(G.needGift, { amount: amt(s.gift) });
  },

  /** Aktualizuje wszystkie paski na stronie (po każdej zmianie koszyka). */
  update(cart) {
    if (!cart) return;
    const merch = Cart.merch(cart);
    $$('f3d-goal').forEach((el) => /** @type {any} */ (el).render?.(merch));
  }
};

class F3DGoal extends HTMLElement {
  static get observedAttributes() {
    return ['data-subtotal'];
  }

  connectedCallback() {
    const value = Number(this.dataset.subtotal) || 0;
    const last = store.get('f3d-goal-last', null);
    if (last != null && last !== value && !reduceMotion()) {
      // Płynne przejście od ostatnio widzianej wartości (np. po odświeżeniu szuflady).
      const s = Goals.state(Number(last));
      this.#setP(s.p);
      requestAnimationFrame(() => requestAnimationFrame(() => this.#setP(Goals.state(value).p)));
    }
    store.set('f3d-goal-last', value);
    this.#positionRunner(Goals.state(value).p);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected) return;
    const value = Number(newValue) || 0;
    store.set('f3d-goal-last', value);
    this.#positionRunner(Goals.state(value).p);
  }

  /** @param {number} p */
  #setP(p) {
    $$('.f3d-goal__fill, .f3d-goal__runner', this).forEach((el) => /** @type {HTMLElement} */ (el).style.setProperty('--p', String(p)));
  }

  /** @param {number} p */
  #positionRunner(p) {
    const runner = $('.f3d-goal__runner', this);
    if (!runner) return;
    runner.classList.toggle('is-far', p > 0.65);
    runner.classList.toggle('is-near', p < 0.2);
  }

  /** @param {number} merch */
  render(merch) {
    const s = Goals.state(merch);
    if (!s.max) return;
    const prevMerch = Number(this.dataset.subtotal) || 0;
    this.dataset.subtotal = String(merch);
    const text = $('[data-f3d-goal-text]', this);
    if (text) text.innerHTML = Goals.text(s);
    const G = I18N.goal || {};
    const track = $('.f3d-goal__track', this);
    track?.setAttribute('aria-valuenow', String(Math.round(s.p * 100)));
    this.#setP(s.p);
    const runner = $('.f3d-goal__runner', this);
    if (runner) runner.setAttribute('data-tip', tpl(G.inCart, { amount: fmt(merch) }));
    const giftMark = $('[data-mark="gift"]', this);
    if (giftMark) {
      const ok = merch >= s.gift;
      giftMark.classList.toggle('is-reached', ok);
      giftMark.setAttribute('data-tip', ok ? G.giftReached : tpl(G.giftFrom, { amount: fmtShort(s.gift) }));
    }
    const shipMark = $('[data-mark="ship"]', this);
    if (shipMark) {
      const ok = merch >= s.free;
      shipMark.classList.toggle('is-reached', ok);
      shipMark.setAttribute('data-tip', ok ? G.shipReached : tpl(G.shipFrom, { amount: fmtShort(s.free) }));
    }
    this.classList.toggle('is-done', merch >= s.max);
    store.set('f3d-goal-last', merch);
    if (prevMerch !== merch) this.#positionRunner(s.p);
  }
}

/** Po każdej zmianie koszyka: gratis, paski, konfetti przy przekroczeniu progu. */
const onCartChanged = debounce(async (source) => {
  let cart;
  try {
    cart = await Cart.fetch();
  } catch (error) {
    return;
  }
  const merch = Cart.merch(cart);
  const prev = Cart.lastMerch;
  Cart.lastMerch = merch;
  Goals.update(cart);
  Locker.refreshAll(cart);

  if (prev != null && merch > prev) {
    const thresholds = [Number(CFG.freeShipping) || 0, CFG.giftVariantId ? Number(CFG.giftThreshold) || 0 : 0].filter((t) => t > 0);
    if (thresholds.some((t) => prev < t && merch >= t)) {
      setTimeout(() => confetti(visibleGoal()), 350);
    }
  }
  if (source !== 'f3d-gift') await Gift.sync(cart);
}, 180);

function visibleGoal() {
  return (
    $$('f3d-goal').find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight;
    }) || null
  );
}

/* ==========================================================================
   Numer paczkomatu (atrybut koszyka "Numer paczkomatu")
   ========================================================================== */
const Locker = {
  key: CFG.lockerKey || 'Numer paczkomatu',
  norm: (s) => String(s || '').toUpperCase().replace(/\s+/g, ''),
  valid: (s) => /^(POP-)?[A-Z]{3}\d{1,4}[A-Z]{0,3}$/.test(Locker.norm(s)),
  current: '',

  /** @param {any} cart */
  refreshAll(cart) {
    this.current = (cart?.attributes && cart.attributes[this.key]) || '';
  },

  /** @param {string} code */
  async save(code) {
    const res = await fetch(`${ROUTES.cartUpdate}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [this.key]: code } })
    });
    if (!res.ok) throw new Error(`locker ${res.status}`);
    const cart = await res.json();
    this.refreshAll(cart);
    Cart.last = cart;
    return cart;
  }
};

class F3DLocker extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.form = $('[data-f3d-locker-form]', this);
    this.input = this.form ? /** @type {HTMLInputElement | null} */ ($('input', this.form)) : null;
    this.saved = $('[data-f3d-locker-saved]', this);
    this.code = $('[data-f3d-locker-code]', this);
    this.msg = $('[data-f3d-locker-msg]', this);
    if (!this.form || !this.input) return;
    Locker.current = this.dataset.saved || Locker.current;

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
    this.input.addEventListener('input', () => {
      this.input?.removeAttribute('aria-invalid');
      if (this.msg) this.msg.textContent = '';
    });
    $('[data-f3d-locker-edit]', this)?.addEventListener('click', () => {
      if (this.saved) this.saved.hidden = true;
      if (this.form) this.form.hidden = false;
      this.input?.focus();
      this.input?.select();
    });
  }

  /** @param {string} text */
  error(text) {
    if (!this.input || !this.msg) return;
    this.input.setAttribute('aria-invalid', 'true');
    this.msg.innerHTML = `<p class="f3d-error">${ICON_ALERT}<span></span></p>`;
    const span = $('span', this.msg);
    if (span) span.textContent = text;
  }

  async submit() {
    if (!this.input) return;
    const val = Locker.norm(this.input.value);
    this.input.value = val;
    if (!val) return this.error(I18N.lockerEmpty);
    if (!Locker.valid(val)) return this.error(I18N.lockerInvalid);
    const btn = $('button[type="submit"]', this.form || this);
    btn?.classList.add('is-loading');
    try {
      await Locker.save(val);
      if (this.code) this.code.textContent = val;
      if (this.saved) this.saved.hidden = false;
      if (this.form) this.form.hidden = true;
      this.dataset.saved = val;
      if (this.msg) this.msg.textContent = '';
      toast(tpl(I18N.lockerSaved, { code: val }), { emoji: '📦' });
    } catch (error) {
      this.error(I18N.lockerError);
    } finally {
      btn?.classList.remove('is-loading');
    }
  }

  /** Pokazuje formularz z komunikatem, gdy trzeba podać numer przed kasą. */
  demand() {
    if (this.closest('details[data-f3d-extras]')) CartUI.setExtras(true);
    if (this.saved) this.saved.hidden = true;
    if (this.form) this.form.hidden = false;
    this.error(I18N.lockerRequired);
    this.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
    setTimeout(() => this.input?.focus({ preventScroll: true }), 250);
  }
}

/** Wymagany numer paczkomatu: przycisk "Do kasy" najpierw prosi o numer. */
function initLockerGuard() {
  if (!CFG.lockerEnabled || !CFG.lockerRequired) return;
  document.addEventListener(
    'click',
    (e) => {
      const btn = /** @type {HTMLElement | null} */ (e.target instanceof Element ? e.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]') : null);
      if (!btn) return;
      const lockers = $$('f3d-locker');
      if (!lockers.length) return;
      const saved = lockers.some((l) => /** @type {HTMLElement} */ (l).dataset.saved) || Locker.current;
      if (saved) return;
      e.preventDefault();
      e.stopPropagation();
      const scope = btn.closest('dialog, cart-items-component');
      const near = scope ? scope.querySelector('f3d-locker') : null;
      const visible = near || lockers.find((l) => l.getBoundingClientRect().width > 0) || lockers[0];
      /** @type {any} */ (visible).demand?.();
    },
    true
  );
}

/* ==========================================================================
   Dostawa: Europe/Warsaw, godzina graniczna, weekendy i święta
   ========================================================================== */
const Delivery = {
  holidays: new Set(String(CFG.holidays || '').match(/\d{4}-\d{2}-\d{2}/g) || []),

  parts(date = new Date()) {
    /** @type {Record<string, string>} */
    const o = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Warsaw',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    })
      .formatToParts(date)
      .forEach((x) => {
        o[x.type] = x.value;
      });
    return { y: +o.year, m: +o.month, d: +o.day, h: +o.hour % 24, min: +o.minute };
  },

  iso: (dt) => dt.toISOString().slice(0, 10),
  addDays: (dt, n) => new Date(dt.getTime() + n * 86400000),
  isWorkday(dt) {
    const wd = dt.getUTCDay();
    return wd !== 0 && wd !== 6 && !this.holidays.has(this.iso(dt));
  },
  nextWorkday(dt) {
    let d = this.addDays(dt, 1);
    let guard = 0;
    while (!this.isWorkday(d) && guard++ < 30) d = this.addDays(d, 1);
    return d;
  },

  compute(now = new Date()) {
    const w = this.parts(now);
    const today = new Date(Date.UTC(w.y, w.m - 1, w.d));
    const [ch, cm] = String(CFG.cutoff || '20:00').split(':').map((n) => Number(n) || 0);
    const nowMin = w.h * 60 + w.min;
    const cutMin = ch * 60 + cm;
    const shipsToday = this.isWorkday(today) && nowMin < cutMin;
    const ship = shipsToday ? today : this.nextWorkday(today);
    let arrive = ship;
    const transit = Math.max(1, Number(CFG.transitDays) || 1);
    for (let i = 0; i < transit; i++) arrive = this.nextWorkday(arrive);
    return { today, ship, arrive, shipsToday, minutesLeft: shipsToday ? cutMin - nowMin : 0 };
  },

  dayShort: new Intl.DateTimeFormat(LOCALE, { weekday: 'short', day: 'numeric', month: '2-digit', timeZone: 'UTC' }),
  dayNum: new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: '2-digit', timeZone: 'UTC' }),
  dayLong: new Intl.DateTimeFormat(LOCALE, { weekday: 'long', timeZone: 'UTC' }),

  rel(dt, today) {
    const diff = Math.round((dt.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return I18N.today;
    if (diff === 1) return I18N.tomorrow;
    if (diff === 2) return I18N.dayAfter;
    const s = this.dayLong.format(dt);
    return s.charAt(0).toUpperCase() + s.slice(1);
  },

  /** @param {number} m */
  minutesText(m) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return h ? tpl(I18N.hoursMinutes, { h, m: mm }) : tpl(I18N.minutes, { m: mm });
  },

  eta(d) {
    const rel = this.rel(d.arrive, d.today);
    const diff = Math.round((d.arrive.getTime() - d.today.getTime()) / 86400000);
    return diff <= 2 ? `${rel.toLowerCase()}, ${this.dayNum.format(d.arrive)}` : this.dayShort.format(d.arrive);
  }
};

class F3DDelivery extends HTMLElement {
  connectedCallback() {
    this.render();
    // Po odświeżeniu sekcji koszyka (morph Horizon) wraca tekst zastępczy: liczymy datę ponownie.
    if (this.dataset.mode === 'eta') {
      this._mo = new MutationObserver(() => this.render());
      this._mo.observe(this, { childList: true, subtree: true, characterData: true });
    } else {
      this._timer = window.setInterval(() => {
        if (!document.hidden) this.render();
      }, 60000);
    }
  }

  disconnectedCallback() {
    if (this._timer) clearInterval(this._timer);
    this._mo?.disconnect();
  }

  render() {
    let d;
    try {
      d = Delivery.compute();
    } catch (error) {
      return;
    }
    if (this.dataset.mode === 'eta') {
      const el = $('[data-f3d-eta]', this);
      const text = tpl(I18N.eta, { date: Delivery.eta(d) });
      if (el && el.textContent !== text) el.textContent = text;
      return;
    }
    const set = (key, when, date) => {
      const w = $(`[data-f3d-when="${key}"]`, this);
      const dt = $(`[data-f3d-date="${key}"]`, this);
      if (w && w.textContent !== when) w.textContent = when;
      if (dt && dt.textContent !== date) dt.textContent = date;
    };
    set('buy', I18N.today, Delivery.dayShort.format(d.today));
    set('ship', Delivery.rel(d.ship, d.today), Delivery.dayShort.format(d.ship));
    set('arrive', Delivery.rel(d.arrive, d.today), Delivery.dayShort.format(d.arrive));

    const cut = $('[data-f3d-cutoff]', this);
    const cutText = $('[data-f3d-cutoff-text]', this);
    if (cut && cutText) {
      cutText.innerHTML = d.shipsToday
        ? tpl(I18N.cutoffToday, { time: escapeHTML(Delivery.minutesText(d.minutesLeft)) })
        : tpl(I18N.cutoffTomorrow, { day: escapeHTML(`${Delivery.rel(d.ship, d.today).toLowerCase()}, ${Delivery.dayNum.format(d.ship)}`) });
      cut.hidden = false;
    }
  }
}

/* ==========================================================================
   Pętle (pasek ogłoszeń, opinie): płynny przesuw bez skoku przy zapętleniu
   ========================================================================== */
class F3DLoop extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this.track = $('.f3d-loop__track', this);
    this.group = this.track ? $('.f3d-loop__group', this.track) : null;
    if (!this.track || !this.group || !this.group.children.length) return;
    this._init = true;
    this.originals = Array.from(this.group.children).map((n) => n.cloneNode(true));
    if (reduceMotion() || CFG.designMode) {
      this.classList.add('is-static');
      if (reduceMotion() && this.tagName === 'F3D-MARQUEE') this.#rotator();
      return;
    }
    let lastW = this.clientWidth;
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(
        debounce(() => {
          if (Math.abs(this.clientWidth - lastW) > 2) {
            lastW = this.clientWidth;
            this.setup();
          }
        }, 150)
      );
      this._ro.observe(this);
    }
    this.setup();
  }

  disconnectedCallback() {
    this._ro?.disconnect();
    if (this._rot) clearInterval(this._rot);
  }

  setup() {
    if (!this.track || !this.group || !this.originals) return;
    this.group.replaceChildren(...this.originals.map((n) => n.cloneNode(true)));
    $$('[data-f3d-clone]', this.track).forEach((n) => n.remove());
    const cw = this.clientWidth || window.innerWidth;
    let guard = 0;
    while (this.group.scrollWidth < cw && guard++ < 10) {
      this.originals.forEach((n) => {
        const c = /** @type {HTMLElement} */ (n.cloneNode(true));
        c.setAttribute('aria-hidden', 'true');
        $$('a, button, [tabindex]', c).forEach((el) => el.setAttribute('tabindex', '-1'));
        if (c.matches('a, button')) c.setAttribute('tabindex', '-1');
        c.removeAttribute('data-shopify-editor-block');
        this.group?.appendChild(c);
      });
    }
    const clone = /** @type {HTMLElement} */ (this.group.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-f3d-clone', '');
    $$('a, button, [tabindex]', clone).forEach((el) => el.setAttribute('tabindex', '-1'));
    $$('[data-shopify-editor-block]', clone).forEach((el) => el.removeAttribute('data-shopify-editor-block'));
    this.track.appendChild(clone);
    const w = this.group.getBoundingClientRect().width;
    const speed = Number(this.dataset.speed) || 40;
    this.style.setProperty('--f3d-loop-dur', `${Math.max(12, w / speed).toFixed(1)}s`);
  }

  /** Tryb bez ruchu: pokazujemy po jednym komunikacie, zmiana co 5 s (tylko przejście przezroczystości). */
  #rotator() {
    const items = $$('.f3d-ticker__item', this);
    if (items.length < 2) return;
    let i = 0;
    items.forEach((el, k) => el.classList.toggle('is-active', k === 0));
    this._rot = window.setInterval(() => {
      if (document.hidden || this.closest('.is-paused')) return;
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, 5000);
  }
}

/** Przycisk pauzy paska ogłoszeń i pętli opinii (WCAG 2.2.2). */
function initLoopToggles() {
  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const announce = target?.closest('[data-f3d-announce-toggle]');
    const loop = target?.closest('[data-f3d-loop-toggle]');
    const btn = /** @type {HTMLElement | null} */ (announce || loop);
    if (!btn) return;
    const scope = announce ? btn.closest('.f3d-announce') : document.querySelector(btn.getAttribute('data-f3d-loop-toggle') || '');
    if (!scope) return;
    const paused = !scope.classList.contains('is-paused');
    scope.classList.toggle('is-paused', paused);
    btn.setAttribute('aria-pressed', String(paused));
    const label = paused ? btn.dataset.labelPlay : btn.dataset.labelPause;
    if (label) btn.setAttribute('aria-label', label);
  });
}

/* ==========================================================================
   Karuzela, zakładki, karty w hero
   ========================================================================== */
class F3DCarousel extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this.track = /** @type {HTMLElement | null} */ ($('[data-f3d-track]', this));
    if (!this.track) return;
    this._init = true;
    this.prev = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-prev]', this));
    this.next = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-next]', this));
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(-1);
      }
    });
    if (!this.track.hasAttribute('tabindex')) this.track.setAttribute('tabindex', '0');
    this.update();
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(debounce(() => this.update(), 120));
      this._ro.observe(this.track);
    }
  }

  disconnectedCallback() {
    this._ro?.disconnect();
  }

  /** @param {number} dir */
  go(dir) {
    if (!this.track) return;
    const first = /** @type {HTMLElement | null} */ (this.track.firstElementChild);
    const step = first ? first.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || '16') : this.track.clientWidth * 0.8;
    const perView = Math.max(1, Math.floor(this.track.clientWidth / step));
    this.track.scrollBy({ left: dir * step * perView, behavior: reduceMotion() ? 'auto' : 'smooth' });
  }

  update() {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max;
  }
}

class F3DTabs extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this.tabs = /** @type {HTMLElement[]} */ ($$('[role="tab"]', this));
    if (!this.tabs.length) return;
    this._init = true;
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        let next = -1;
        if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = this.tabs.length - 1;
        if (next < 0) return;
        e.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      });
    });
  }

  /** @param {number} index */
  select(index) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.setAttribute('tabindex', on ? '0' : '-1');
      const panel = document.getElementById(tab.getAttribute('aria-controls') || '');
      if (!panel) return;
      panel.hidden = !on;
      if (on) {
        panel.classList.remove('is-pre');
        if (document.documentElement.classList.contains('f3d-anim')) {
          panel.classList.remove('is-in');
          void panel.offsetWidth;
          panel.classList.add('is-in');
        }
      }
    });
  }
}

class F3DHeroCards extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.addEventListener('click', (e) => {
      const dot = e.target instanceof Element ? e.target.closest('[data-f3d-hero-color]') : null;
      if (!dot) return;
      const card = /** @type {HTMLElement | null} */ (dot.closest('[data-f3d-hero-card]'));
      if (!card) return;
      card.dataset.locked = '1';
      this.show(card, dot.getAttribute('data-f3d-hero-color') || '');
    });
    this.addEventListener('pointerover', (e) => {
      const c = /** @type {HTMLElement | null} */ (e.target instanceof Element ? e.target.closest('[data-f3d-hero-card]') : null);
      if (c) c.dataset.hover = '1';
    });
    this.addEventListener('pointerout', (e) => {
      const c = /** @type {HTMLElement | null} */ (e.target instanceof Element ? e.target.closest('[data-f3d-hero-card]') : null);
      if (c && !(e.relatedTarget instanceof Node && c.contains(e.relatedTarget))) delete c.dataset.hover;
    });
    if (reduceMotion() || CFG.designMode) return;
    let tick = 0;
    this._timer = window.setInterval(() => {
      if (document.hidden || this.closest('.is-offscreen')) return;
      const cards = /** @type {HTMLElement[]} */ ($$('[data-f3d-hero-card]', this)).filter((c) => $$('[data-f3d-hero-color]', c).length > 1);
      if (!cards.length) return;
      const card = cards[tick % cards.length];
      tick += 1;
      if (card.dataset.locked || card.dataset.hover) return;
      const dots = $$('[data-f3d-hero-color]', card);
      const cur = dots.findIndex((d) => d.getAttribute('aria-pressed') === 'true');
      const next = dots[(cur + 1) % dots.length];
      this.show(card, next.getAttribute('data-f3d-hero-color') || '');
    }, 900);
  }

  disconnectedCallback() {
    if (this._timer) clearInterval(this._timer);
  }

  /** @param {HTMLElement} card @param {string} color */
  show(card, color) {
    $$('[data-shot]', card).forEach((s) => s.classList.toggle('is-active', s.getAttribute('data-shot') === color));
    $$('[data-f3d-hero-color]', card).forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-f3d-hero-color') === color)));
  }
}

/* ==========================================================================
   Karta produktu Fajne3D: kolory, szybkie dodawanie, mini-wybór opcji
   ========================================================================== */
class F3DCard extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const sw = target.closest('[data-f3d-swatch]');
      if (sw) {
        e.preventDefault();
        this.selectColor(sw.getAttribute('data-f3d-swatch') || '');
        return;
      }
      if (target.closest('[data-f3d-add]')) {
        e.preventDefault();
        this.onAdd(/** @type {HTMLButtonElement} */ (target.closest('[data-f3d-add]')));
        return;
      }
      const opt = target.closest('[data-f3d-opt]');
      if (opt) {
        e.preventDefault();
        this.pickOption(Number(opt.getAttribute('data-f3d-opt-index')), opt.getAttribute('data-f3d-opt') || '');
        return;
      }
      if (target.closest('[data-f3d-picker-close]')) {
        e.preventDefault();
        this.closePicker();
        return;
      }
      if (target.closest('[data-f3d-picker-add]')) {
        e.preventDefault();
        this.addSelected(/** @type {HTMLButtonElement} */ (target.closest('[data-f3d-picker-add]')));
      }
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && $('.f3d-picker', this)) {
        this.closePicker();
        /** @type {HTMLElement | null} */ ($('[data-f3d-add]', this))?.focus();
      }
    });
  }

  get data() {
    if (this._data) return this._data;
    try {
      this._data = JSON.parse($('script[data-f3d-variants]', this)?.textContent || '{}');
    } catch (e) {
      this._data = { options: [], variants: [] };
    }
    return this._data;
  }

  get current() {
    const id = Number($('[data-f3d-add]', this)?.getAttribute('data-variant-id'));
    return (this.data.variants || []).find((v) => v.id === id) || (this.data.variants || [])[0];
  }

  /** @param {string} color */
  selectColor(color) {
    const ci = Number(this.dataset.colorIndex);
    if (ci < 0 || Number.isNaN(ci)) return;
    const cur = this.current;
    const vs = this.data.variants || [];
    const same = (v) => v.options[ci] === color;
    const keepOthers = (v) => cur && v.options.every((o, i) => i === ci || o === cur.options[i]);
    const v = vs.find((x) => same(x) && keepOthers(x) && x.available) || vs.find((x) => same(x) && x.available) || vs.find(same);
    if (!v) return;
    this.applyVariant(v);
    $$('[data-f3d-swatch]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-f3d-swatch') === color)));
    const dot = /** @type {HTMLElement | null} */ ($('[data-f3d-add-dot]', this));
    const sw = /** @type {HTMLElement | null} */ ($(`[data-f3d-swatch="${CSS.escape(color)}"]`, this));
    if (dot && sw) dot.style.setProperty('--c', sw.style.getPropertyValue('--c'));
  }

  /** @param {any} v */
  applyVariant(v) {
    const add = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-add]', this));
    if (add) {
      add.setAttribute('data-variant-id', String(v.id));
      add.disabled = !v.available;
    }
    const now = $('[data-f3d-now]', this);
    if (now) now.textContent = fmt(v.price);
    const was = /** @type {HTMLElement | null} */ ($('[data-f3d-was]', this));
    const wasVal = $('[data-f3d-was-value]', this);
    const sep = /** @type {HTMLElement | null} */ ($('[data-f3d-sep]', this));
    const sale = v.compare > v.price;
    if (was) was.hidden = !sale;
    if (sep) sep.hidden = !sale;
    if (wasVal && sale) wasVal.textContent = fmt(v.compare);
    $('[data-f3d-price]', this)?.classList.toggle('is-sale', sale);
    this.classList.toggle('is-variant-soldout', !v.available);
    const img = /** @type {HTMLImageElement | null} */ ($('.f3d-card__img--main', this));
    if (img && v.image) {
      const src = String(v.image);
      const withWidth = (w) => (src.includes('width=') ? src.replace(/width=\d+/, `width=${w}`) : `${src}${src.includes('?') ? '&' : '?'}width=${w}`);
      img.srcset = [360, 600, 900].map((w) => `${withWidth(w)} ${w}w`).join(', ');
      img.src = withWidth(600);
    }
  }

  /** @param {HTMLButtonElement} btn */
  async onAdd(btn) {
    if (btn.disabled) return;
    if (this.dataset.mode === 'choose') {
      this.openPicker();
      return;
    }
    const id = btn.getAttribute('data-variant-id');
    if (!id) return;
    await addWithFeedback(btn, id);
  }

  openPicker() {
    if ($('.f3d-picker', this)) return;
    const opts = this.data.options || [];
    const cur = this.current;
    this._sel = cur ? [...cur.options] : opts.map(() => '');
    const media = $('.f3d-card__media', this) || this;
    const panel = document.createElement('div');
    panel.className = 'f3d-picker';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', I18N.chooseOptions || 'Wybierz opcje');
    panel.innerHTML = `<div class="f3d-picker__head"><span></span><button type="button" class="f3d-iconbtn f3d-picker__close" data-f3d-picker-close aria-label="${escapeHTML(I18N.close || 'Zamknij')}"><svg class="f3d-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button></div><div data-f3d-picker-groups></div><button type="button" class="f3d-btn f3d-btn--primary f3d-btn--sm f3d-btn--block" data-f3d-picker-add></button>`;
    $('.f3d-picker__head span', panel).textContent = this.querySelector('.f3d-card__title')?.textContent?.trim() || '';
    media.appendChild(panel);
    this.renderPicker();
    /** @type {HTMLElement | null} */ ($('[data-f3d-opt]', panel))?.focus();
    this._outside = (e) => {
      if (!(e.target instanceof Node) || !this.contains(e.target)) this.closePicker();
    };
    setTimeout(() => document.addEventListener('pointerdown', this._outside), 0);
  }

  renderPicker() {
    const panel = $('.f3d-picker', this);
    if (!panel) return;
    const opts = this.data.options || [];
    const vs = this.data.variants || [];
    const groups = $('[data-f3d-picker-groups]', panel);
    if (!groups) return;
    groups.innerHTML = opts
      .map((name, oi) => {
        const values = [...new Set(vs.map((v) => v.options[oi]))];
        const chips = values
          .map((val) => {
            const possible = vs.some((v) => v.options[oi] === val && v.available && v.options.every((o, i) => i === oi || !this._sel[i] || i > oi || o === this._sel[i]));
            const pressed = this._sel[oi] === val;
            return `<button type="button" class="f3d-optchip" data-f3d-opt="${escapeHTML(val)}" data-f3d-opt-index="${oi}" aria-pressed="${pressed}"${possible ? '' : ' disabled'}>${escapeHTML(val)}</button>`;
          })
          .join('');
        return `<div class="f3d-picker__group" role="group" aria-label="${escapeHTML(name)}"><span class="f3d-picker__label">${escapeHTML(name)}</span><div class="f3d-picker__opts">${chips}</div></div>`;
      })
      .join('');
    const v = vs.find((x) => x.options.every((o, i) => o === this._sel[i]));
    const addBtn = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-picker-add]', panel));
    if (addBtn) {
      addBtn.disabled = !v || !v.available;
      addBtn.textContent = v && v.available ? `${I18N.addLong || 'Dodaj do koszyka'} · ${fmt(v.price)}` : I18N.unavailable || 'Niedostępny';
      if (v) addBtn.setAttribute('data-variant-id', String(v.id));
      else addBtn.removeAttribute('data-variant-id');
    }
  }

  /** @param {number} index @param {string} value */
  pickOption(index, value) {
    if (!this._sel) return;
    this._sel[index] = value;
    const vs = this.data.variants || [];
    if (!vs.some((v) => v.available && v.options.every((o, i) => o === this._sel[i]))) {
      const fit = vs.find((v) => v.available && v.options[index] === value);
      if (fit) this._sel = [...fit.options];
    }
    this.renderPicker();
    /** @type {HTMLElement | null} */ ($(`[data-f3d-opt-index="${index}"][aria-pressed="true"]`, this))?.focus();
  }

  /** @param {HTMLButtonElement} btn */
  async addSelected(btn) {
    const id = btn.getAttribute('data-variant-id');
    if (!id) return;
    const ok = await addWithFeedback(btn, id);
    if (ok) this.closePicker();
  }

  closePicker() {
    $('.f3d-picker', this)?.remove();
    if (this._outside) document.removeEventListener('pointerdown', this._outside);
  }
}

/**
 * Dodanie do koszyka z informacją na przycisku ("Dodaję…" → "Dodano ✓").
 * @param {HTMLButtonElement} btn
 * @param {string | number} variantId
 */
async function addWithFeedback(btn, variantId) {
  if (btn.classList.contains('is-loading')) return false;
  btn.classList.add('is-loading');
  btn.setAttribute('aria-busy', 'true');
  const ok = await Cart.add(variantId, { source: btn });
  btn.classList.remove('is-loading');
  btn.removeAttribute('aria-busy');
  if (ok) {
    btn.classList.add('is-done');
    setTimeout(() => btn.classList.remove('is-done'), 1400);
  }
  return ok;
}

/** Małe wiersze produktów (upsell, pusty koszyk): przycisk + dodaje od razu. */
function initQuickAddButtons() {
  document.addEventListener('click', (e) => {
    const btn = /** @type {HTMLButtonElement | null} */ (e.target instanceof Element ? e.target.closest('[data-f3d-quick-add]') : null);
    if (!btn) return;
    e.preventDefault();
    addWithFeedback(btn, btn.getAttribute('data-f3d-quick-add') || '');
  });
}

/* ==========================================================================
   Strona produktu: personalizacja, długi opis, odświeżanie bloków po zmianie wariantu
   ========================================================================== */
class F3DPerso extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this.input = /** @type {HTMLInputElement | null} */ ($('input', this));
    this.count = $('[data-f3d-perso-count]', this);
    this.preview = /** @type {HTMLElement | null} */ ($('[data-f3d-perso-preview]', this));
    this.tag = $('[data-f3d-perso-tag]', this);
    if (!this.input) return;
    this._init = true;
    const max = Number(this.dataset.max) || this.input.maxLength || 15;
    const update = () => {
      if (!this.input) return;
      const len = [...this.input.value].length;
      if (this.count) {
        this.count.textContent = tpl(I18N.persoCount, { count: len, max });
        this.count.classList.toggle('is-max', len >= max);
      }
      const val = this.input.value.trim();
      if (this.preview) this.preview.hidden = !val;
      if (this.tag) this.tag.textContent = val;
    };
    this.input.addEventListener('input', update);
    update();
  }
}

class F3DMore extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this.inner = /** @type {HTMLElement | null} */ ($('.f3d-more__inner', this));
    this.btn = /** @type {HTMLButtonElement | null} */ ($('.f3d-more__btn', this));
    if (!this.inner || !this.btn) return;
    this._init = true;
    this.btn.addEventListener('click', () => this.toggle());
    this.measure();
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(debounce(() => this.measure(), 150));
      this._ro.observe(this);
    }
  }

  disconnectedCallback() {
    this._ro?.disconnect();
  }

  measure() {
    if (!this.inner || !this.btn || this.classList.contains('is-open')) return;
    const lh = parseFloat(getComputedStyle(this.inner).lineHeight) || 24;
    const limit = lh * (Number(this.dataset.lines) || 7);
    this.inner.style.setProperty('--f3d-more-h', `${Math.round(limit)}px`);
    const clamped = this.inner.scrollHeight > limit + lh;
    this.classList.toggle('is-clamped', clamped);
    this.btn.hidden = !clamped;
  }

  toggle() {
    if (!this.btn) return;
    const open = !this.classList.contains('is-open');
    this.classList.toggle('is-open', open);
    this.btn.setAttribute('aria-expanded', String(open));
    const label = $('[data-f3d-more-label]', this.btn);
    if (label) label.textContent = open ? I18N.showLess : I18N.showMore;
    if (!open) this.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' });
    this.classList.toggle('is-clamped', !open);
  }
}

/** Bloki z data-f3d-sync odświeżają się razem z wariantem (HTML z odpowiedzi Horizon). */
function initVariantSync() {
  document.addEventListener(
    StandardEvents.productSelect,
    (event) => {
      const e = /** @type {any} */ (event);
      const target = e.target instanceof Element ? e.target : null;
      if (!target || target.closest('product-card, f3d-card')) return;
      const scope = target.closest('.shopify-section, dialog');
      if (!scope || !$('[data-f3d-sync]', scope)) return;
      e.promise
        ?.then((/** @type {any} */ result) => {
          const html = result?.detail?.html;
          if (!html || typeof html.querySelector !== 'function') return;
          $$('[data-f3d-sync]', scope).forEach((el) => {
            const key = el.getAttribute('data-f3d-sync');
            const fresh = key ? html.querySelector(`[data-f3d-sync="${CSS.escape(key)}"]`) : null;
            if (fresh) el.innerHTML = fresh.innerHTML;
          });
        })
        .catch(() => {});
    },
    true
  );
}

/* ==========================================================================
   Kolekcja: kopiowanie kodu i odliczanie promocji
   ========================================================================== */
function initCopy() {
  document.addEventListener('click', async (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-f3d-copy]') : null;
    if (!btn) return;
    const code = btn.getAttribute('data-f3d-copy') || '';
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(code);
      else {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      toast(tpl(I18N.copied, { code }));
    } catch (error) {
      toast(I18N.copyError, { type: 'error' });
    }
  });
}

/** Zamienia "RRRR-MM-DD GG:MM" (czas polski) na znacznik czasu UTC. */
function warsawTime(str) {
  const m = String(str || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?/);
  if (!m) return NaN;
  const [, y, mo, d, h = '23', mi = '59'] = m;
  const guess = Date.UTC(+y, +mo - 1, +d, +h, +mi);
  const p = Delivery.parts(new Date(guess));
  const asWarsaw = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min);
  return guess - (asWarsaw - guess);
}

class F3DCountdown extends HTMLElement {
  connectedCallback() {
    this.end = warsawTime(this.dataset.end || '');
    this.box = /** @type {HTMLElement | null} */ ($('[data-f3d-countdown]', this));
    if (!this.box || Number.isNaN(this.end)) return;
    this.tick();
    this._timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this._timer) clearInterval(this._timer);
  }

  tick() {
    if (!this.box || this.end == null) return;
    const left = this.end - Date.now();
    if (left <= 0) {
      clearInterval(this._timer);
      if (this.dataset.autoHide === 'true' && !CFG.designMode) {
        const wrap = /** @type {HTMLElement | null} */ (this.closest('.f3d-promo-wrap'));
        if (wrap) wrap.hidden = true;
      } else this.box.hidden = true;
      return;
    }
    const s = Math.floor(left / 1000);
    const vals = { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
    Object.entries(vals).forEach(([k, v]) => {
      const el = $(`[data-unit="${k}"]`, this.box || this);
      if (el) el.textContent = k === 'd' ? String(v) : String(v).padStart(2, '0');
    });
    this.box.hidden = false;
  }
}

/* ==========================================================================
   Wideo z jednym przyciskiem odtwórz/pauza, statystyki "drukowane", ostatnio oglądane
   ========================================================================== */
class F3DVideo extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this.video = /** @type {HTMLVideoElement | null} */ ($('video', this));
    this.btn = /** @type {HTMLButtonElement | null} */ ($('.f3d-video__play', this));
    if (!this.video || !this.btn) return;
    this._init = true;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.loop = true;
    this.addEventListener('click', (e) => {
      if (e.target instanceof Element && e.target.closest('a')) return;
      this.toggle();
    });
    this.addEventListener('pointermove', () => {
      this.classList.add('is-awake');
      clearTimeout(this._awake);
      this._awake = setTimeout(() => this.classList.remove('is-awake'), 1500);
    });
    this.video.addEventListener('play', () => this.sync(true));
    this.video.addEventListener('pause', () => this.sync(false));
  }

  toggle() {
    if (!this.video) return;
    if (this.video.paused) this.video.play().catch(() => this.sync(false));
    else this.video.pause();
  }

  /** @param {boolean} playing */
  sync(playing) {
    if (!this.btn) return;
    this.classList.toggle('is-playing', playing);
    this.btn.setAttribute('aria-pressed', String(playing));
    const label = playing ? this.btn.dataset.labelPause : this.btn.dataset.labelPlay;
    if (label) this.btn.setAttribute('aria-label', label);
  }
}

class F3DPrint extends HTMLElement {
  connectedCallback() {
    if (this._init || !document.documentElement.classList.contains('f3d-anim') || !('IntersectionObserver' in window)) return;
    this._init = true;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          this.classList.add('is-printing');
          io.disconnect();
        });
      },
      { threshold: 0.4 }
    );
    io.observe(this);
  }
}

const Viewed = {
  key: 'f3d-viewed',
  get() {
    try {
      const own = JSON.parse(window.localStorage.getItem(this.key) || '[]');
      const horizon = JSON.parse(window.localStorage.getItem('viewedProducts') || '[]');
      return [...new Set([...(Array.isArray(own) ? own : []), ...(Array.isArray(horizon) ? horizon : [])].map(String))];
    } catch (e) {
      return [];
    }
  },
  /** @param {string} id */
  add(id) {
    if (!id) return;
    try {
      const list = this.get().filter((x) => x !== String(id));
      list.unshift(String(id));
      window.localStorage.setItem(this.key, JSON.stringify(list.slice(0, 12)));
    } catch (e) {
      /* brak dostępu do pamięci przeglądarki */
    }
  }
};

class F3DRecent extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.load();
  }

  async load() {
    const current = this.dataset.current || '';
    const limit = Number(this.dataset.limit) || 8;
    const ids = Viewed.get().filter((id) => id && id !== current).slice(0, limit);
    if (!ids.length || CFG.designMode) return;
    const slot = $('[data-f3d-recent-slot]', this);
    if (!slot) return;
    try {
      const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', 'f3d-recent-results');
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const items = Array.from(doc.querySelectorAll('.f3d-recent-results li[data-product-id]')).sort(
        (a, b) => ids.indexOf(a.getAttribute('data-product-id') || '') - ids.indexOf(b.getAttribute('data-product-id') || '')
      );
      if (!items.length) return;
      slot.replaceChildren(...items.map((li) => document.importNode(li, true)));
      const section = /** @type {HTMLElement | null} */ (this.closest('.f3d-recent'));
      if (section) section.hidden = false;
      /** @type {any} */ ($('f3d-carousel', this))?.update?.();
    } catch (error) {
      /* brak listy nie jest błędem krytycznym */
    }
  }
}

/* ==========================================================================
   Strona produktu Fajne3D: warianty, cena, galeria, zoom, lightbox, przyklejony pasek
   ========================================================================== */
const SVG = {
  trash: '<svg class="f3d-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/></svg>',
  check2: '<svg class="f3d-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="m8 12.5 2.8 2.8L16.5 9.5"/></svg>',
  close: '<svg class="f3d-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>',
  tag: '<svg class="f3d-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 12V4h8l10 10-8 8L3 12Z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>',
  bag: '<svg class="f3d-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5.5 8h13l-1 12.5h-11L5.5 8Z"/><path d="M9 8V7a3 3 0 0 1 6 0v1"/></svg>'
};
const pluralPL = new Intl.PluralRules('pl-PL');

class F3DProduct extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    try {
      this.data = JSON.parse($('script[data-f3d-product-json]', this)?.textContent || '{}');
    } catch (e) {
      this.data = { options: [], variants: [] };
    }
    this.variants = this.data.variants || [];
    this.form = /** @type {HTMLFormElement | null} */ ($('[data-f3d-product-form]', this));
    this.low = Number(this.dataset.low) || 5;
    this.colorIndex = Number(this.dataset.colorIndex);
    Viewed.add(this.dataset.productId || '');

    if (this.form) {
      this.form.addEventListener('change', (e) => {
        const t = /** @type {HTMLInputElement} */ (e.target);
        if (t.name && t.name.startsWith('f3d-option-')) this.onOption(t);
      });
      this.form.addEventListener('submit', (e) => this.submit(e));
      this.form.addEventListener('click', (e) => {
        const b = e.target instanceof Element ? e.target.closest('[data-f3d-qty]') : null;
        if (!b) return;
        const input = /** @type {HTMLInputElement | null} */ ($('[data-f3d-qty-input]', this.form || this));
        if (!input) return;
        const max = Number(input.max) || 999;
        input.value = String(Math.min(max, Math.max(1, (Number(input.value) || 1) + Number(b.getAttribute('data-f3d-qty')))));
        this.syncQty();
      });
      $('[data-f3d-qty-input]', this.form)?.addEventListener('change', () => this.syncQty());
      this.syncQty();
    }
    this.initGallery();
    this.initLightbox();
    this.initSticky();
    this.initModels();
    this.initVideos();
  }

  /** @returns {string[]} */
  selected() {
    const count = (this.data.options || []).length;
    const out = [];
    for (let i = 0; i < count; i++) {
      const checked = /** @type {HTMLInputElement | null} */ ($(`[data-f3d-option="${i}"] input:checked`, this));
      out.push(checked ? checked.value : '');
    }
    return out;
  }

  /** @param {HTMLInputElement} input */
  onOption(input) {
    const idx = Number(input.closest('[data-f3d-option]')?.getAttribute('data-f3d-option'));
    const sel = this.selected();
    let v = this.variants.find((x) => x.options.every((o, i) => o === sel[i]));
    if (!v) {
      v = this.variants.find((x) => x.available && x.options[idx] === sel[idx]) || this.variants.find((x) => x.options[idx] === sel[idx]);
      if (v) {
        v.options.forEach((val, i) => {
          const r = /** @type {HTMLInputElement | null} */ ($(`[data-f3d-option="${i}"] input[value="${CSS.escape(val)}"]`, this));
          if (r) r.checked = true;
        });
      }
    }
    if (v) this.apply(v);
  }

  /** @param {any} v */
  apply(v) {
    this.current = v;
    const input = /** @type {HTMLInputElement | null} */ ($('[data-f3d-variant-input]', this));
    if (input) input.value = String(v.id);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(v.id));
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* adres bez zmian */
    }

    const price = $('[data-f3d-price]', this);
    if (price) price.innerHTML = this.priceHTML(v);
    const sticker = $('[data-f3d-sticker]', this);
    if (sticker) {
      const pct = v.compare > v.price ? Math.round(((v.compare - v.price) * 100) / v.compare) : 0;
      sticker.innerHTML = pct && CFG.saleSticker !== false
        ? `<span class="f3d-sticker f3d-sticker--lg" aria-hidden="true"><svg viewBox="0 0 100 100" focusable="false"><path d="M50.0 1.0L59.6 8.1L71.3 5.9L76.8 16.4L88.3 19.4L88.7 31.3L97.8 39.1L93.0 50.0L97.8 60.9L88.7 68.7L88.3 80.6L76.8 83.6L71.3 94.1L59.6 91.9L50.0 99.0L40.4 91.9L28.7 94.1L23.2 83.6L11.7 80.6L11.3 68.7L2.2 60.9L7.0 50.0L2.2 39.1L11.3 31.3L11.7 19.4L23.2 16.4L28.7 5.9L40.4 8.1Z" fill="var(--f3d-accent)" stroke="var(--f3d-ink)" stroke-width="3" stroke-linejoin="round"/></svg><span class="f3d-sticker__txt"><small>${escapeHTML(I18N.cheaper || '')}</small>−${pct}%</span></span>`
        : '';
    }

    const sel = v.options;
    $$('[data-f3d-option]', this).forEach((fs) => {
      const i = Number(fs.getAttribute('data-f3d-option'));
      const label = $('[data-f3d-option-label]', fs);
      if (label) label.textContent = sel[i] || '';
      $$('input', fs).forEach((r) => {
        const inp = /** @type {HTMLInputElement} */ (r);
        const ok = this.variants.some((x) => x.available && x.options[i] === inp.value && x.options.every((o, j) => j === i || o === sel[j]));
        inp.closest('label')?.classList.toggle('is-soldout', !ok);
      });
    });

    const add = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-add-main]', this));
    if (add) {
      add.disabled = !v.available;
      add.innerHTML = v.available ? `${SVG.bag}<span data-f3d-add-label>${escapeHTML(I18N.addLong || '')}</span>` : `<span data-f3d-add-label>${escapeHTML(I18N.soldOut || '')}</span>`;
    }
    const qty = /** @type {HTMLInputElement | null} */ ($('[data-f3d-qty-input]', this));
    if (qty) {
      if (v.stock != null && v.stock > 0) qty.max = String(v.stock);
      else qty.removeAttribute('max');
      this.syncQty();
    }
    const delivery = /** @type {HTMLElement | null} */ ($('[data-f3d-delivery-wrap]', this));
    if (delivery) delivery.hidden = !v.available;

    const sticky = $('[data-f3d-sticky]', this);
    if (sticky) {
      const sp = $('[data-f3d-sticky-price]', sticky);
      const color = this.colorIndex >= 0 ? v.options[this.colorIndex] : '';
      if (sp) sp.textContent = `${fmt(v.price)}${color ? ` · ${color}` : ''}`;
      const sb = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-sticky-add]', sticky));
      if (sb) {
        sb.disabled = !v.available;
        sb.textContent = v.available ? I18N.addLong || '' : I18N.soldOut || '';
      }
      const si = /** @type {HTMLImageElement | null} */ ($('.f3d-sticky-atc__image', sticky));
      if (si && v.image) {
        si.srcset = '';
        si.src = v.image;
      }
    }

    if (v.media) {
      const slide = $(`.f3d-gallery__slide[data-media-id="${v.media}"]`, this);
      if (slide) this.goTo(Number(slide.getAttribute('data-index')) || 0);
    }
  }

  /** @param {any} v */
  priceHTML(v) {
    const sale = v.compare > v.price;
    let html = `<span class="f3d-price__now">${escapeHTML(fmt(v.price))}</span>`;
    if (sale) html += `<s class="f3d-price__was"><span class="visually-hidden">${escapeHTML(I18N.regularPrice || '')} </span>${escapeHTML(fmt(v.compare))}</s>`;
    html += `<span class="f3d-price__vat">${escapeHTML(I18N.withVat || '')}</span>`;
    if (sale) {
      const pct = Math.round(((v.compare - v.price) * 100) / v.compare);
      html += `<span class="f3d-save f3d-save--lg">${SVG.tag}${escapeHTML(tpl(I18N.cheaperBy, { amount: fmtShort(v.compare - v.price), percent: pct }))}</span>`;
    }
    if (!v.available) html += `<span class="f3d-avail is-out">${SVG.close}${escapeHTML(I18N.productSoldOut || '')}</span>`;
    else if (v.stock != null && v.stock > 0 && v.stock <= this.low) {
      const form = pluralPL.select(v.stock);
      const key = form === 'one' ? 'lowOne' : form === 'few' ? 'lowFew' : 'lowMany';
      html += `<span class="f3d-avail is-low"><span class="f3d-avail__dot" aria-hidden="true"></span>${escapeHTML(tpl(I18N[key], { count: v.stock }))}</span>`;
    } else if (this.dataset.showInStock === 'true') html += `<span class="f3d-avail is-in">${SVG.check2}${escapeHTML(I18N.available || '')}</span>`;
    return html;
  }

  syncQty() {
    const input = /** @type {HTMLInputElement | null} */ ($('[data-f3d-qty-input]', this));
    if (!input) return;
    const max = Number(input.max) || 999;
    const val = Math.min(max, Math.max(1, Number(input.value) || 1));
    input.value = String(val);
    const dec = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-qty="-1"]', this));
    const inc = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-qty="1"]', this));
    if (dec) dec.disabled = val <= 1;
    if (inc) inc.disabled = val >= max;
  }

  /** @param {SubmitEvent} e */
  async submit(e) {
    e.preventDefault();
    if (!this.form) return;
    const btn = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-add-main]', this));
    const msg = /** @type {HTMLElement | null} */ ($('[data-f3d-buy-msg]', this));
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
    if (!this.form.reportValidity()) return;
    if (msg) msg.hidden = true;
    const fd = new FormData(this.form);
    Array.from(fd.keys()).forEach((k) => {
      if (k.startsWith('f3d-option-')) fd.delete(k);
    });
    btn.classList.add('is-loading');
    const result = await Cart.addForm(fd, btn);
    btn.classList.remove('is-loading');
    if (result.ok) {
      btn.classList.add('is-done');
      setTimeout(() => btn.classList.remove('is-done'), 1400);
    } else if (msg && result.message) {
      msg.textContent = result.message;
      msg.hidden = false;
    }
  }

  /* ---------- Galeria ---------- */
  initGallery() {
    this.track = /** @type {HTMLElement | null} */ ($('[data-f3d-gtrack]', this));
    if (!this.track) return;
    this.slides = /** @type {HTMLElement[]} */ ($$('.f3d-gallery__slide', this.track));
    this.thumbs = /** @type {HTMLElement[]} */ ($$('[data-f3d-thumb]', this));
    this.count = $('[data-f3d-gcount]', this);
    this.index = 0;
    let raf = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.onScroll());
      },
      { passive: true }
    );
    this.thumbs.forEach((t) => {
      t.addEventListener('click', () => this.goTo(Number(t.getAttribute('data-f3d-thumb'))));
      t.addEventListener('keydown', (e) => {
        const i = Number(t.getAttribute('data-f3d-thumb'));
        let n = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % this.thumbs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + this.thumbs.length) % this.thumbs.length;
        if (n < 0) return;
        e.preventDefault();
        this.thumbs[n].focus();
        this.goTo(n);
      });
    });
    $('[data-f3d-gprev]', this)?.addEventListener('click', () => this.goTo(this.index - 1));
    $('[data-f3d-gnext]', this)?.addEventListener('click', () => this.goTo(this.index + 1));

    // Mały zoom (1,3×) za kursorem, tylko przy myszce
    if (finePointer()) {
      $$('[data-f3d-zoom]', this).forEach((z) => {
        const el = /** @type {HTMLElement} */ (z);
        el.addEventListener('pointermove', (e) => {
          const r = el.getBoundingClientRect();
          el.style.setProperty('--zx', `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
          el.style.setProperty('--zy', `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
          el.classList.add('is-zooming');
        });
        el.addEventListener('pointerleave', () => el.classList.remove('is-zooming'));
      });
    }
    const initial = this.variants.find((x) => String(x.id) === ($('[data-f3d-variant-input]', this)?.getAttribute('value') || ''));
    if (initial?.media) {
      const slide = $(`.f3d-gallery__slide[data-media-id="${initial.media}"]`, this);
      const i = Number(slide?.getAttribute('data-index'));
      if (i > 0) requestAnimationFrame(() => this.goTo(i, true));
    }
  }

  /** @param {number} i @param {boolean} [instant] */
  goTo(i, instant = false) {
    if (!this.track || !this.slides?.length) return;
    const n = this.slides.length;
    const idx = ((i % n) + n) % n;
    this.track.scrollTo({ left: this.slides[idx].offsetLeft, behavior: instant || reduceMotion() ? 'auto' : 'smooth' });
    this.setIndex(idx);
  }

  onScroll() {
    if (!this.track || !this.slides?.length) return;
    const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
    if (i !== this.index) this.setIndex(Math.min(this.slides.length - 1, Math.max(0, i)));
  }

  /** @param {number} i */
  setIndex(i) {
    this.index = i;
    if (this.count) this.count.textContent = `${i + 1} / ${this.slides?.length || 1}`;
    this.thumbs?.forEach((t, k) => (k === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
    this.thumbs?.[i]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    this.syncVideos();
  }

  /* ---------- Filmy w galerii: lecą same (bez dźwięku, w pętli) tylko na widocznym slajdzie ---------- */
  initVideos() {
    this.videoBoxes = /** @type {HTMLElement[]} */ ($$('[data-f3d-video]', this));
    if (!this.videoBoxes.length) return;
    this.galleryVisible = true;
    const gallery = $('[data-f3d-gallery]', this);
    if (gallery && 'IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => {
        this.galleryVisible = en.isIntersecting;
        this.syncVideos();
      }, { threshold: 0.25 }).observe(gallery);
    }
    this.videoBoxes.forEach((box) => {
      const vid = /** @type {HTMLVideoElement | null} */ ($('video', box));
      if (!vid) return;
      vid.muted = true;
      vid.playsInline = true;
      vid.addEventListener('play', () => box.classList.remove('is-paused'));
      vid.addEventListener('pause', () => box.classList.add('is-paused'));
      const sound = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-sound]', box));
      sound?.addEventListener('click', (e) => {
        e.stopPropagation();
        vid.muted = !vid.muted;
        sound.setAttribute('aria-pressed', String(!vid.muted));
        sound.setAttribute('aria-label', (vid.muted ? sound.dataset.labelOn : sound.dataset.labelOff) || '');
        if (vid.paused) vid.play().catch(() => {});
      });
      // Klik w film: pauza / wznowienie
      box.addEventListener('click', () => {
        if (vid.paused) {
          box.dataset.userPaused = '';
          vid.play().catch(() => {});
        } else {
          box.dataset.userPaused = '1';
          vid.pause();
        }
      });
    });
    this.syncVideos();
  }

  syncVideos() {
    if (!this.videoBoxes?.length) return;
    this.slides?.forEach((s, k) => {
      const box = /** @type {HTMLElement | null} */ ($('[data-f3d-video]', s));
      const vid = /** @type {HTMLVideoElement | null} */ (box ? $('video', box) : null);
      if (!box || !vid) return;
      const active = k === this.index && this.galleryVisible !== false;
      if (active && !box.dataset.userPaused && !reduceMotion()) {
        vid.play().catch(() => box.classList.add('is-paused'));
      } else {
        if (!active) delete box.dataset.userPaused;
        vid.pause();
      }
    });
  }

  /* ---------- Powiększenie (lightbox) ---------- */
  initLightbox() {
    this.lb = /** @type {HTMLDialogElement | null} */ ($('[data-f3d-lightbox]', this));
    if (!this.lb) return;
    this.zooms = /** @type {HTMLElement[]} */ ($$('[data-f3d-zoom]', this));
    this.zooms.forEach((z, k) => z.addEventListener('click', () => this.openLb(k)));
    $('[data-f3d-lb-close]', this.lb)?.addEventListener('click', () => this.lb?.close());
    $('[data-f3d-lb-prev]', this.lb)?.addEventListener('click', () => this.showLb(this.lbIndex - 1));
    $('[data-f3d-lb-next]', this.lb)?.addEventListener('click', () => this.showLb(this.lbIndex + 1));
    this.lb.addEventListener('click', (e) => {
      if (e.target === this.lb || (e.target instanceof Element && e.target.classList.contains('f3d-lightbox__stage'))) this.lb?.close();
    });
    this.lb.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.showLb(this.lbIndex + 1);
      if (e.key === 'ArrowLeft') this.showLb(this.lbIndex - 1);
    });
    this.lb.addEventListener('close', () => this.zooms?.[this.lbIndex]?.focus({ preventScroll: true }));

    // Kliknięcie w zdjęcie przybliża 2,5×, ruch myszką lub palcem przesuwa przybliżony fragment
    const img = /** @type {HTMLImageElement | null} */ ($('[data-f3d-lb-img]', this.lb));
    if (img) {
      const aim = (/** @type {PointerEvent} */ e) => {
        const r = img.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
        const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
        img.style.setProperty('--lx', `${x.toFixed(1)}%`);
        img.style.setProperty('--ly', `${y.toFixed(1)}%`);
      };
      img.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!img.classList.contains('is-zoomed')) aim(/** @type {any} */ (e));
        img.classList.toggle('is-zoomed');
      });
      img.addEventListener('pointermove', (e) => {
        if (!img.classList.contains('is-zoomed')) return;
        if (e.pointerType === 'mouse' || e.buttons) aim(e);
      });
    }
  }

  /** @param {number} k */
  openLb(k) {
    if (!this.lb) return;
    this.showLb(k);
    try {
      this.lb.showModal();
    } catch (e) {
      this.lb.setAttribute('open', '');
    }
  }

  /** @param {number} k */
  showLb(k) {
    if (!this.lb || !this.zooms?.length) return;
    const n = this.zooms.length;
    this.lbIndex = ((k % n) + n) % n;
    const z = this.zooms[this.lbIndex];
    const img = /** @type {HTMLImageElement | null} */ ($('[data-f3d-lb-img]', this.lb));
    const src = /** @type {HTMLImageElement | null} */ ($('img', z));
    if (img) {
      img.classList.remove('is-zoomed');
      img.src = z.getAttribute('data-full') || src?.currentSrc || '';
      img.alt = src?.alt || '';
    }
    const count = $('[data-f3d-lb-count]', this.lb);
    if (count) count.textContent = `${this.lbIndex + 1} / ${n}`;
  }

  /* ---------- Przyklejony pasek zakupu (telefon) ---------- */
  initSticky() {
    const bar = /** @type {HTMLElement | null} */ ($('[data-f3d-sticky]', this));
    const main = $('[data-f3d-add-main]', this);
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    const btn = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-sticky-add]', bar));
    btn?.addEventListener('click', () => /** @type {HTMLButtonElement} */ (main).click());
    const io = new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', past);
      bar.setAttribute('aria-hidden', String(!past));
      if (btn) btn.tabIndex = past ? 0 : -1;
      document.body.classList.toggle('f3d-has-sticky', past);
    });
    io.observe(main);
  }

  /* ---------- Modele 3D (Shopify model viewer) ---------- */
  initModels() {
    const models = $$('model-viewer', this);
    if (!models.length || !window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors || !window.Shopify?.ModelViewerUI) return;
          models.forEach((m) => {
            try {
              new window.Shopify.ModelViewerUI(m);
            } catch (e) {
              /* model bez interfejsu */
            }
          });
        }
      }
    ]);
  }
}

/** "Klienci kupowali również": sekcja dociągana z Product Recommendations API. */
class F3DRecs extends HTMLElement {
  connectedCallback() {
    if (this._init || this.children.length || !this.dataset.url) return;
    this._init = true;
    // Sekcja jest ukryta do czasu załadowania, więc ładujemy po chwili bezczynności (a nie na przewinięcie).
    const run = () => this.load();
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 800);
  }

  async load() {
    try {
      const res = await fetch(this.dataset.url || '', { credentials: 'same-origin' });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('f3d-recs');
      if (!fresh || !fresh.children.length) return;
      this.replaceChildren(...Array.from(fresh.children).map((n) => document.importNode(n, true)));
      const section = /** @type {HTMLElement | null} */ (this.closest('section'));
      if (section) section.hidden = false;
    } catch (error) {
      /* bez poleceń strona działa normalnie */
    }
  }
}

/* ==========================================================================
   Lista produktów: filtry, cena, sortowanie, "Załaduj więcej" (bez przeładowania strony)
   ========================================================================== */
class F3DCollection extends HTMLElement {
  /** Kolory wybrane w filtrze motywu (bez Search & Discovery). @type {Set<string>} */
  #colors = new Set();

  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.sectionId = this.dataset.sectionId || '';
    this.baseUrl = this.dataset.url || window.location.pathname;
    this.#syncAllPrices();
    this.#observeMore();

    this.addEventListener('change', (e) => {
      const t = /** @type {HTMLElement} */ (e.target);
      if (t.matches('[data-f3d-local-color]')) {
        this.#onLocalColor(/** @type {HTMLInputElement} */ (t));
        return;
      }
      if (t.matches('[data-f3d-sort]')) {
        this.apply(null);
        return;
      }
      const form = /** @type {HTMLFormElement | null} */ (t.closest('[data-f3d-filters]'));
      if (!form || t.matches('[data-f3d-range]')) return;
      if (t.matches('[data-f3d-price-input]')) this.#clampPrice(form);
      this.apply(form);
    });
    this.addEventListener('input', (e) => {
      const t = /** @type {HTMLInputElement} */ (e.target);
      if (!t.matches('[data-f3d-range]')) return;
      const box = /** @type {HTMLElement | null} */ (t.closest('[data-f3d-price]'));
      if (!box) return;
      this.#fromRange(box);
      clearTimeout(this._rangeTimer);
      this._rangeTimer = setTimeout(() => this.apply(/** @type {HTMLFormElement} */ (box.closest('form'))), 350);
    });
    this.addEventListener('submit', (e) => {
      const form = /** @type {HTMLFormElement} */ (e.target);
      if (!form.matches('[data-f3d-filters]')) return;
      e.preventDefault();
      this.apply(form);
    });
    this.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      const link = /** @type {HTMLAnchorElement | null} */ (t.closest('a[data-f3d-link]'));
      if (link) {
        e.preventDefault();
        this.load(link.href);
        return;
      }
      const more = /** @type {HTMLAnchorElement | null} */ (t.closest('a[data-f3d-more]'));
      if (more) {
        e.preventDefault();
        this.loadMore(more);
        return;
      }
      if (t.closest('[data-f3d-filters-open]')) {
        const d = /** @type {HTMLDialogElement | null} */ ($('dialog.f3d-fdrawer', this));
        if (d && !d.open) {
          try {
            d.showModal();
          } catch (err) {
            d.setAttribute('open', '');
          }
        }
        return;
      }
      if (t.closest('[data-f3d-filters-close]')) {
        /** @type {HTMLDialogElement | null} */ ($('dialog.f3d-fdrawer', this))?.close();
        return;
      }
      if (t instanceof HTMLDialogElement && t.classList.contains('f3d-fdrawer')) {
        const r = t.getBoundingClientRect();
        const ev = /** @type {MouseEvent} */ (e);
        if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) t.close();
      }
    });
  }

  /** @param {HTMLElement} box */
  #fromRange(box) {
    const [a, b] = $$('[data-f3d-range]', box).map((i) => Number(/** @type {HTMLInputElement} */ (i).value));
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const minIn = /** @type {HTMLInputElement | null} */ ($('[data-f3d-price-input="min"]', box));
    const maxIn = /** @type {HTMLInputElement | null} */ ($('[data-f3d-price-input="max"]', box));
    const max = Number(box.dataset.max) || 0;
    if (minIn) minIn.value = lo > 0 ? String(lo) : '';
    if (maxIn) maxIn.value = hi < max ? String(hi) : '';
    this.#paint(box, lo, hi);
  }

  /** @param {HTMLFormElement} form */
  #clampPrice(form) {
    const box = /** @type {HTMLElement | null} */ ($('[data-f3d-price]', form));
    if (!box) return;
    const max = Number(box.dataset.max) || 0;
    const minIn = /** @type {HTMLInputElement | null} */ ($('[data-f3d-price-input="min"]', box));
    const maxIn = /** @type {HTMLInputElement | null} */ ($('[data-f3d-price-input="max"]', box));
    let lo = Math.max(0, Math.min(max, Number(minIn?.value) || 0));
    let hi = maxIn?.value ? Math.max(0, Math.min(max, Number(maxIn.value))) : max;
    if (lo > hi) [lo, hi] = [hi, lo];
    if (minIn) minIn.value = lo > 0 ? String(lo) : '';
    if (maxIn) maxIn.value = hi < max ? String(hi) : '';
    const ranges = /** @type {HTMLInputElement[]} */ ($$('[data-f3d-range]', box));
    if (ranges[0]) ranges[0].value = String(lo);
    if (ranges[1]) ranges[1].value = String(hi);
    this.#paint(box, lo, hi);
  }

  #syncAllPrices() {
    $$('[data-f3d-price]', this).forEach((box) => {
      const [a, b] = $$('[data-f3d-range]', box).map((i) => Number(/** @type {HTMLInputElement} */ (i).value));
      this.#paint(/** @type {HTMLElement} */ (box), Math.min(a, b), Math.max(a, b));
    });
  }

  /** @param {HTMLElement} box @param {number} lo @param {number} hi */
  #paint(box, lo, hi) {
    const max = Number(box.dataset.max) || 1;
    const fill = /** @type {HTMLElement | null} */ ($('[data-f3d-range-fill]', box));
    if (fill) {
      fill.style.left = `${(lo / max) * 100}%`;
      fill.style.right = `${100 - (hi / max) * 100}%`;
    }
    const val = $('[data-f3d-price-value]', box);
    if (val) val.textContent = `${lo} zł – ${hi} zł`;
  }

  /** Buduje adres z formularza filtrów i sortowania. @param {HTMLFormElement | null} form */
  urlFrom(form) {
    const params = new URLSearchParams();
    const source = form || /** @type {HTMLFormElement | null} */ ($('[data-f3d-filters]', this));
    if (source) {
      for (const [key, value] of new FormData(source)) {
        if (String(value).trim() !== '') params.append(key, String(value));
      }
    }
    const sort = /** @type {HTMLSelectElement | null} */ ($('[data-f3d-sort]', this));
    if (sort?.value) params.set('sort_by', sort.value);
    const qs = params.toString();
    return `${this.baseUrl}${qs ? `?${qs}` : ''}`;
  }

  /** @param {HTMLFormElement | null} form */
  apply(form) {
    this.load(this.urlFrom(form));
  }

  /** @param {string} url */
  async fetchSection(url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', this.sectionId);
    this._abort?.abort();
    this._abort = new AbortController();
    const res = await fetch(u.toString(), { signal: this._abort.signal, credentials: 'same-origin' });
    if (!res.ok) throw new Error(`collection ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = doc.querySelector('f3d-collection');
    if (!fresh) throw new Error('collection markup');
    return fresh;
  }

  /** @param {string} url */
  async load(url) {
    const results = /** @type {HTMLElement | null} */ ($('[data-f3d-results]', this));
    results?.classList.add('is-loading');
    results?.setAttribute('aria-busy', 'true');
    const activeId = document.activeElement instanceof HTMLElement && this.contains(document.activeElement) ? document.activeElement.id : '';
    try {
      const fresh = await this.fetchSection(url);
      const openState = new Map($$('details[data-f3d-filter]', this).map((d) => [`${d.closest('[data-f3d-filters-slot]')?.getAttribute('data-f3d-filters-slot')}|${d.getAttribute('data-f3d-filter')}`, /** @type {HTMLDetailsElement} */ (d).open]));
      const newResults = $('[data-f3d-results]', fresh);
      if (results && newResults) results.innerHTML = newResults.innerHTML;
      $$('[data-f3d-filters-slot]', this).forEach((slot) => {
        const key = slot.getAttribute('data-f3d-filters-slot');
        const fs = fresh.querySelector(`[data-f3d-filters-slot="${key}"]`);
        if (fs) slot.innerHTML = fs.innerHTML;
      });
      const foot = $('.f3d-fdrawer__foot', this);
      const newFoot = $('.f3d-fdrawer__foot', fresh);
      if (foot && newFoot) foot.innerHTML = newFoot.innerHTML;
      $$('details[data-f3d-filter]', this).forEach((d) => {
        const key = `${d.closest('[data-f3d-filters-slot]')?.getAttribute('data-f3d-filters-slot')}|${d.getAttribute('data-f3d-filter')}`;
        if (openState.has(key)) /** @type {HTMLDetailsElement} */ (d).open = Boolean(openState.get(key));
      });
      this.#syncAllPrices();
      this.#applyLocal();
      this.#observeMore();
      window.history.replaceState({}, '', url);
      if (activeId) /** @type {HTMLElement | null} */ (document.getElementById(activeId))?.focus({ preventScroll: true });
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') {
        window.location.href = url;
      }
    } finally {
      results?.classList.remove('is-loading');
      results?.setAttribute('aria-busy', 'false');
    }
  }

  /** @param {HTMLAnchorElement} btn */
  async loadMore(btn) {
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    try {
      const fresh = await this.fetchSection(btn.href);
      const grid = $('[data-f3d-grid]', this);
      const newGrid = $('[data-f3d-grid]', fresh);
      if (grid && newGrid) {
        const firstNew = /** @type {HTMLElement | null} */ (newGrid.firstElementChild);
        grid.append(...Array.from(newGrid.children));
        firstNew?.querySelector('a')?.focus({ preventScroll: true });
      }
      const lm = $('[data-f3d-loadmore]', this);
      const newLm = $('[data-f3d-loadmore]', fresh);
      if (lm && newLm) lm.innerHTML = newLm.innerHTML;
      if (this.#colors.size) this.#applyLocal();
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') window.location.href = btn.href;
    } finally {
      btn.classList.remove('is-loading');
    }
  }

  disconnectedCallback() {
    this._io?.disconnect();
  }

  /** Kolejna strona doładowuje się sama, gdy klient zbliży się do końca listy. */
  #observeMore() {
    this._io?.disconnect();
    const lm = $('[data-f3d-loadmore]', this);
    if (!lm || !('IntersectionObserver' in window)) return;
    this._io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        const more = /** @type {HTMLAnchorElement | null} */ ($('a[data-f3d-more]', this));
        if (!more || more.classList.contains('is-loading')) return;
        // Po doładowaniu obserwujemy od nowa: jeśli koniec listy dalej jest blisko, ładujemy następną stronę.
        this.loadMore(more).then(() => this.#observeMore());
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this._io.observe(lm);
  }

  /** @param {HTMLInputElement} input */
  async #onLocalColor(input) {
    // Jedno kółko może łączyć kilka nazw o tym samym odcieniu ("czerwony|mystic red").
    input.value.split('|').filter(Boolean).forEach((name) => {
      if (input.checked) this.#colors.add(name);
      else this.#colors.delete(name);
    });
    if (this.#colors.size) await this.#loadAll();
    this.#applyLocal();
  }

  /** Filtr kolorów w przeglądarce działa na wszystkich produktach, więc najpierw doładowujemy resztę stron. */
  async #loadAll() {
    let guard = 0;
    let more = /** @type {HTMLAnchorElement | null} */ ($('a[data-f3d-more]', this));
    while (more && guard < 20) {
      guard += 1;
      await this.loadMore(more);
      more = /** @type {HTMLAnchorElement | null} */ ($('a[data-f3d-more]', this));
    }
  }

  #applyLocal() {
    const sel = this.#colors;
    $$('[data-f3d-local-color]', this).forEach((i) => {
      const inp = /** @type {HTMLInputElement} */ (i);
      const names = inp.value.split('|').filter(Boolean);
      inp.checked = names.length > 0 && names.every((n) => sel.has(n));
    });
    let shown = 0;
    $$('[data-f3d-grid] > li', this).forEach((li) => {
      const colors = li.querySelector('[data-f3d-colors]')?.getAttribute('data-f3d-colors') || '';
      const ok = !sel.size || Array.from(sel).some((c) => colors.includes(`|${c}|`));
      /** @type {HTMLElement} */ (li).hidden = !ok;
      if (ok) shown += 1;
    });
    const count = /** @type {HTMLElement | null} */ ($('.f3d-toolbar__count', this));
    if (count) {
      if (count.dataset.f3dOrig === undefined) count.dataset.f3dOrig = count.textContent || '';
      if (sel.size) {
        const form = pluralPL.select(shown);
        const text = form === 'one' ? I18N.countOne : form === 'few' ? I18N.countFew : I18N.countMany;
        count.textContent = String(text || '').replace(/\d+/, String(shown));
      } else count.textContent = count.dataset.f3dOrig;
    }
    const show = /** @type {HTMLElement | null} */ ($('.f3d-fdrawer__foot [data-f3d-filters-close]', this));
    if (show) {
      if (show.dataset.f3dOrig === undefined) show.dataset.f3dOrig = (show.textContent || '').trim();
      if (sel.size) {
        const form = pluralPL.select(shown);
        const text = form === 'one' ? I18N.showOne : form === 'few' ? I18N.showFew : I18N.showMany;
        show.textContent = String(text || '').replace(/\d+/, String(shown));
      } else show.textContent = show.dataset.f3dOrig;
    }
    const lm = /** @type {HTMLElement | null} */ ($('[data-f3d-loadmore]', this));
    if (lm) lm.hidden = sel.size > 0;
  }
}

/* ==========================================================================
   Animacje wejścia, przechylanie kart, pauza animacji poza ekranem, podpowiedzi dotykowe
   ========================================================================== */
function initReveal() {
  const anim = document.documentElement.classList.contains('f3d-anim');
  const els = $$('[data-f3d-reveal], [data-f3d-stagger]');
  if (!anim || !('IntersectionObserver' in window)) return;
  els.forEach((el) => {
    if (el.hasAttribute('data-f3d-stagger')) {
      Array.from(el.children).forEach((child, i) => {
        const c = /** @type {HTMLElement} */ (child);
        if (!c.style.getPropertyValue('--i')) c.style.setProperty('--i', String(i % 12));
      });
    }
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.92 && r.bottom > 0) {
      el.classList.add('is-in', 'is-instant');
      return;
    }
    el.classList.add('is-pre');
  });
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.remove('is-pre');
        en.target.classList.add('is-in');
        io.unobserve(en.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  els.filter((el) => el.classList.contains('is-pre')).forEach((el) => io.observe(el));
}

function initTilt() {
  if (CFG.tilt === false || reduceMotion() || !finePointer()) return;
  document.addEventListener(
    'pointermove',
    (e) => {
      const el = /** @type {HTMLElement | null} */ (e.target instanceof Element ? e.target.closest('[data-tilt]') : null);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--rx', `${(-y * 8).toFixed(2)}deg`);
      el.style.setProperty('--ry', `${(x * 10).toFixed(2)}deg`);
      el.style.setProperty('--gx', `${((x + 0.5) * 100).toFixed(1)}%`);
      el.style.setProperty('--gy', `${((y + 0.5) * 100).toFixed(1)}%`);
    },
    { passive: true }
  );
  document.addEventListener('pointerout', (e) => {
    const el = /** @type {HTMLElement | null} */ (e.target instanceof Element ? e.target.closest('[data-tilt]') : null);
    if (el && !(e.relatedTarget instanceof Node && el.contains(e.relatedTarget))) {
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    }
  });
}

function initOffscreenPause() {
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => entries.forEach((en) => en.target.classList.toggle('is-offscreen', !en.isIntersecting)),
    { rootMargin: '120px 0px' }
  );
  $$('.f3d-hero, .f3d-spinband, .f3d-reviews2, .f3d-announce, .f3d-infostrip, .f3d-statsband').forEach((el) => io.observe(el));
}

function initTapTips() {
  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target.closest('.f3d-ship__step, .f3d-goal__mark, .f3d-goal__runner, .f3d-emoji') : null;
    $$('.is-tip').forEach((x) => {
      if (x !== t) x.classList.remove('is-tip');
    });
    if (t && !finePointer()) t.classList.toggle('is-tip');
  });
}

/**
 * Horizon na komputerze przewija `.page-wrapper` (html i body mają overflow: hidden),
 * a na telefonie całe okno. Zwraca element, który faktycznie się przewija.
 */
function scroller() {
  const pw = /** @type {HTMLElement | null} */ (document.querySelector('.page-wrapper'));
  if (pw && pw.scrollHeight > pw.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(pw).overflowY)) return pw;
  return null;
}

function initToTop() {
  const btn = /** @type {HTMLButtonElement | null} */ ($('[data-f3d-totop]'));
  if (!btn) return;
  const onScroll = () => {
    const pw = scroller();
    const y = pw ? pw.scrollTop : window.scrollY;
    btn.hidden = y < 900;
  };
  document.addEventListener('scroll', debounce(onScroll, 80), { passive: true, capture: true });
  onScroll();
  btn.addEventListener('click', () => {
    const behavior = reduceMotion() ? 'auto' : 'smooth';
    const pw = scroller();
    if (pw) pw.scrollTo({ top: 0, behavior });
    else window.scrollTo({ top: 0, behavior });
    const main = /** @type {HTMLElement | null} */ (document.querySelector('#MainContent'));
    if (main) {
      if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
    }
  });
}

/* ==========================================================================
   Nasłuch zdarzeń koszyka (Horizon + nasze) i start
   ========================================================================== */
function initCartEvents() {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const e = /** @type {any} */ (event);
    const isAdd = e.action === 'add';
    e.promise
      ?.then((/** @type {any} */ result) => {
        const detail = result?.detail || {};
        if (isAdd && !detail.didError && detail.source !== 'f3d-gift') {
          toast(I18N.added, { emoji: '🛒' });
        }
        onCartChanged(detail.source);
      })
      .catch(() => onCartChanged('error'));
  });
  if (StandardEvents.cartDiscountUpdate) {
    document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => {
      /** @type {any} */ (event).promise?.then(() => onCartChanged('discount')).catch(() => {});
    });
  }
  // Stan początkowy: zapamiętaj wartość koszyka i popraw gratis, jeśli koszyk był zmieniony gdzie indziej.
  Cart.fetch()
    .then((cart) => {
      Cart.lastMerch = Cart.merch(cart);
      Locker.refreshAll(cart);
      return Gift.sync(cart);
    })
    .catch(() => {});
}

/* ==========================================================================
   Koszyk Fajne3D (szuflada i strona): ilość, usuwanie, kod rabatowy, notatka.
   Po zmianie wysyłamy standardowe zdarzenie, a Horizon (cart-items-component) podmienia sekcje.
   ========================================================================== */
const CartUI = {
  extrasOpen: Boolean(store.get('f3d-extras-open', false)),
  /** @type {Set<string>} */
  busy: new Set(),

  /** Sekcje koszyka na stronie (szuflada i/lub strona koszyka) do odświeżenia w tej samej odpowiedzi. */
  sectionIds() {
    return $$('cart-items-component[data-section-id]')
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId || '')
      .filter(Boolean);
  },

  /** @param {string} path @param {Record<string, any>} body */
  async post(path, body) {
    const res = await fetch(`${path}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && !data.status, data };
  },

  /** @param {HTMLElement} line @param {number} qty */
  async setLine(line, qty) {
    const key = line.dataset.f3dLine;
    if (!key || this.busy.has(key)) return;
    this.busy.add(key);
    line.classList.add('is-updating');
    // Jak w Horizon: ilość 0 to akcja 'remove' (zdarzenie 'update' wymaga ilości > 0).
    const deferred = Cart.announce(document, qty > 0 ? 'update' : 'remove', [{ id: key, quantity: qty }], 'cart');
    try {
      const { ok, data } = await this.post(ROUTES.cartChange || '/cart/change', { id: key, quantity: qty, sections: this.sectionIds() });
      if (!ok) {
        const cart = await Cart.fetch().catch(() => null);
        if (cart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, itemCount: cart.item_count, source: 'f3d-line' }
          });
        } else deferred.reject(new Error('cart'));
        toast(data.description || data.message || I18N.updateError, { type: 'error' });
        return;
      }
      Cart.last = data;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { didError: false, items: data.items, itemCount: data.item_count, sections: data.sections, source: 'f3d-line' }
      });
    } catch (error) {
      deferred.reject(error);
      toast(I18N.networkError, { type: 'error' });
    } finally {
      this.busy.delete(key);
      line.classList.remove('is-updating');
    }
  },

  /**
   * Ustawia listę kodów rabatowych koszyka.
   * @param {string[]} codes
   * @param {{ added?: string, removed?: string }} [info]
   */
  async setCodes(codes, info = {}) {
    const deferred = CartDiscountUpdateEvent.createPromise();
    try {
      document.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: codes.map((code) => ({ code })), promise: deferred.promise }));
    } catch (error) {
      console.warn('[f3d] Nie udało się wysłać zdarzenia rabatu', error);
    }
    try {
      const { ok, data } = await this.post(ROUTES.cartUpdate, { discount: codes.join(','), sections: this.sectionIds() });
      if (!ok) throw new Error(data.description || 'discount');
      Cart.last = data;
      deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data), detail: { sections: data.sections } });
      const added = info.added;
      if (added) {
        const found = (data.discount_codes || []).find((d) => String(d.code).toLowerCase() === added.toLowerCase());
        if (!found || found.applicable === false) toast(tpl(I18N.codeInvalid, { code: added }), { type: 'error' });
        else toast(tpl(I18N.codeAdded, { code: found.code }), { emoji: '🏷️' });
      } else if (info.removed) toast(tpl(I18N.codeRemoved, { code: info.removed }), { emoji: '🏷️' });
    } catch (error) {
      deferred.reject(error);
      toast(I18N.networkError, { type: 'error' });
    }
  },

  /** @param {HTMLElement} el */
  codesOf(el) {
    const box = /** @type {HTMLElement | null} */ (el.closest('[data-f3d-code-box]'));
    return String(box?.dataset.codes || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
  },

  saveNote: debounce((/** @type {string} */ note) => {
    CartUI.post(ROUTES.cartUpdate, { note }).catch(() => {});
  }, 500),

  /** Rozwinięcie "Paczkomat, kod rabatowy i notatka" przetrwa odświeżenie szuflady. */
  syncExtras() {
    $$('details[data-f3d-extras]').forEach((d) => {
      const det = /** @type {HTMLDetailsElement} */ (d);
      if (det.open !== this.extrasOpen) det.open = this.extrasOpen;
    });
  },

  /** @param {boolean} open */
  setExtras(open) {
    this.extrasOpen = open;
    store.set('f3d-extras-open', open);
    this.syncExtras();
  }
};

function initCartUI() {
  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const set = /** @type {HTMLElement | null} */ (t.closest('[data-f3d-line-set]'));
    if (set) {
      const line = /** @type {HTMLElement | null} */ (set.closest('[data-f3d-line]'));
      if (line && !set.hasAttribute('disabled')) CartUI.setLine(line, Math.max(0, Number(set.dataset.f3dLineSet) || 0));
      return;
    }
    const rm = /** @type {HTMLElement | null} */ (t.closest('[data-f3d-code-remove]'));
    if (rm) {
      const code = rm.dataset.f3dCodeRemove || '';
      CartUI.setCodes(CartUI.codesOf(rm).filter((c) => c.toLowerCase() !== code.toLowerCase()), { removed: code });
      return;
    }
    const summary = t.closest('details[data-f3d-extras] > summary');
    if (summary) {
      e.preventDefault();
      CartUI.setExtras(!(/** @type {HTMLDetailsElement} */ (summary.parentElement).open));
    }
  });

  document.addEventListener('change', (e) => {
    const input = e.target instanceof HTMLInputElement && e.target.matches('[data-f3d-line-qty]') ? e.target : null;
    if (!input) return;
    const line = /** @type {HTMLElement | null} */ (input.closest('[data-f3d-line]'));
    if (!line) return;
    let qty = Math.max(0, Math.floor(Number(input.value)));
    if (!Number.isFinite(qty)) qty = 1;
    if (input.max) qty = Math.min(qty, Number(input.max));
    CartUI.setLine(line, qty);
  });

  document.addEventListener('submit', (e) => {
    const form = e.target instanceof HTMLFormElement && e.target.matches('[data-f3d-code-form]') ? e.target : null;
    if (!form) return;
    e.preventDefault();
    const input = /** @type {HTMLInputElement | null} */ (form.elements.namedItem('code'));
    const code = (input?.value || '').trim();
    if (!code) {
      toast(I18N.codeEmpty, { type: 'error' });
      input?.focus();
      return;
    }
    const codes = CartUI.codesOf(form).filter((c) => c.toLowerCase() !== code.toLowerCase());
    if (input) input.value = '';
    CartUI.setCodes([...codes, code], { added: code });
  });

  document.addEventListener('input', (e) => {
    const t = e.target instanceof HTMLTextAreaElement && e.target.matches('[data-f3d-cart-note]') ? e.target : null;
    if (t) CartUI.saveNote(t.value);
  });

  CartUI.syncExtras();
  new MutationObserver(() => CartUI.syncExtras()).observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['open']
  });
}

const ELEMENTS = {
  'f3d-goal': F3DGoal,
  'f3d-locker': F3DLocker,
  'f3d-delivery': F3DDelivery,
  'f3d-loop': F3DLoop,
  'f3d-marquee': class extends F3DLoop {},
  'f3d-carousel': F3DCarousel,
  'f3d-tabs': F3DTabs,
  'f3d-hero-cards': F3DHeroCards,
  'f3d-card': F3DCard,
  'f3d-perso': F3DPerso,
  'f3d-more': F3DMore,
  'f3d-countdown': F3DCountdown,
  'f3d-video': F3DVideo,
  'f3d-print': F3DPrint,
  'f3d-recent': F3DRecent,
  'f3d-recs': F3DRecs,
  'f3d-product': F3DProduct,
  'f3d-collection': F3DCollection
};

Object.entries(ELEMENTS).forEach(([name, ctor]) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
});

function boot() {
  const safe = (fn) => {
    try {
      fn();
    } catch (error) {
      console.warn('[f3d]', error);
    }
  };
  safe(initReveal);
  safe(initTilt);
  safe(initOffscreenPause);
  safe(initTapTips);
  safe(initToTop);
  safe(initCopy);
  safe(initLoopToggles);
  safe(initQuickAddButtons);
  safe(initVariantSync);
  safe(initLockerGuard);
  safe(initCartEvents);
  safe(initCartUI);
  // Intro (słowa w hero, karty) tylko raz przy wejściu: potem zwykły stan.
  setTimeout(() => document.documentElement.classList.remove('f3d-intro'), 2600);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();

window.F3D = Object.assign(window.F3D || {}, { toast, confetti, Cart, Goals, Delivery, fmt });
